import React from 'react';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import ManualClassroomsProvider from '@teetsh/app/src/containers/App/context/ClassroomsContext/ManualClassroomsContext';
import tw from 'twin.macro';
import { ROItemList } from '@teetsh/app/src/components/ROItemList';
import { FichesDePrepSearchResult } from '@teetsh/app/src/containers/Search/types';
import { getSeanceAttachmentUrl } from '@teetsh/app/src/utils/urls';
import { BookOpenIcon as BookOpen } from '@heroicons/react/24/outline';
import { DocumentArrowDownIcon as DocumentDownload } from '@heroicons/react/24/outline';
import { PaperClipIcon as PaperClip } from '@heroicons/react/24/outline';
import Line from '@teetsh/app/src/containers/Search/components/Line';
import { BriefcaseIcon as Briefcase } from '@heroicons/react/24/outline';
import { Bars4Icon as ViewList } from '@heroicons/react/24/outline';
import { PhaseComponent } from '@teetsh/app/src/containers/Sequences/Print/FicheDePrep/Phase';
import { useTranslation } from 'react-i18next';
import { PreviewCustomFieldList } from '../components/PreviewCustomField';
import { CustomFieldDefinitionWithTargets } from 'types/customField';

interface Props {
  fdp: FichesDePrepSearchResult;
  customFieldDefinitions: CustomFieldDefinitionWithTargets[];
}

export function FdpPreview(props: Props) {
  const { fdp, customFieldDefinitions } = props;
  const { boLabels } = useUser();
  const { t } = useTranslation(['common']);

  const isBoEmpty =
    fdp?.bo?.matiere?.length === 0 &&
    fdp?.bo?.domaine?.length === 0 &&
    fdp?.bo?.competence?.length === 0 &&
    fdp?.bo?.goals?.length === 0;
  return (
    <div tw="p-6">
      <ManualClassroomsProvider classrooms={[]}>
        <div>
          <div tw="flex items-center">
            <div tw="text-xl font-bold flex-grow">{fdp.title}</div>
          </div>
        </div>
        <div tw="border-b w-full my-4" />

        <div tw="grid grid-cols-12 gap-x-2 gap-y-4 mb-8">
          {!isBoEmpty && (
            <Line icon={BookOpen}>
              <div tw="space-y-1">
                <ROItemList
                  label={boLabels.matiereSingularName}
                  labelPlural={boLabels.matierePluralName}
                  items={(fdp?.bo?.matiere || []).map(m => m.value)}
                />
                <ROItemList
                  label={boLabels.domaineSingularName}
                  labelPlural={boLabels.domainePluralName}
                  items={(fdp?.bo?.domaine || []).map(m => m.value)}
                />
                <ROItemList
                  label={boLabels.competenceSingularName}
                  labelPlural={boLabels.competencePluralName}
                  items={(fdp?.bo?.competence || []).map(m => m.value)}
                />
                <ROItemList
                  label={boLabels.objectifSingularName}
                  labelPlural={boLabels.objectifPluralName}
                  items={(fdp?.bo?.goals || []).map(m => m.value)}
                />
              </div>
            </Line>
          )}
          {(fdp.customFields || []).length > 0 && (
            <Line icon={ViewList}>
              <PreviewCustomFieldList
                customFields={fdp.customFields}
                customFieldDefinitions={customFieldDefinitions}
              />
            </Line>
          )}

          {(fdp.materials || []).length > 0 && (
            <Line icon={Briefcase}>
              <ROItemList
                items={fdp.materials.map(m => m.value)}
                label={t('common:search.results.material_one')}
                labelPlural={t('common:search.results.material_other')}
              />
            </Line>
          )}
          {(fdp?.attachments || []).length > 0 && (
            <Line icon={PaperClip}>
              <div tw="space-x-2">
                {fdp.attachments.map(a => (
                  <a
                    onClick={e => e.stopPropagation()}
                    href={getSeanceAttachmentUrl(
                      a.s3Path,
                      a.s3Filename,
                      a.label,
                    )}
                    rel="noreferrer noopener"
                    css={[
                      tw`text-neutral-moderate hover:underline break-all font-medium`,
                    ]}
                    target="_blank"
                  >
                    <div
                      key={a.label}
                      tw="rounded-xl bg-neutral-quiet hover:bg-neutral-moderate p-2 py-1 inline-flex text-xs items-center cursor-pointer"
                    >
                      <DocumentDownload tw="h-4 w-4 mr-1 text-neutral-moderate" />
                      {a.label}
                    </div>
                  </a>
                ))}
              </div>
            </Line>
          )}
        </div>
        <div tw="space-y-8">
          {(fdp.phases || []).map(p => (
            <div key={p.id}>
              <PhaseComponent phase={p} />
            </div>
          ))}
        </div>
      </ManualClassroomsProvider>
    </div>
  );
}
